import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  Text, Spinner, Badge, Dropdown, Option, Button, Field, Input,
  makeStyles, tokens, Tooltip as FluentTooltip,
  MessageBar, MessageBarBody, MessageBarTitle,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar, Legend,
} from 'recharts';
import { fetchLotes } from '../api/lotes.api';
import { onlyCampoLotes } from '../utils/lote-uso';
import { fetchModules } from '../api/modules.api';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { parseLoteZoneId, loteZoneId } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { CycleCombobox } from '../components/shared/CycleCombobox';
import { fetchRanchos } from '../api/rancho.api';
import { fetchIrrigationLogs, fetchIrrigationKpi, fetchRecentIrrigationLogs, createIrrigationLog, type CreateIrrigationInput, type IrrigationStockDeductStatus } from '../api/irrigation.api';
import { KpiCard } from '../components/shared/KpiCard';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useT } from '../i18n';
import { useToast } from '../hooks/useToast';
import { NumberInput } from '../components/shared/NumberInput';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { usePageTitle } from '../hooks/usePageTitle';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import type { Lote, IrrigationLog, IrrigationTimestamps, IrrigationMethod } from '../types/api.types';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: '24px' },
  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  kpiRow:  { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  grid:    {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '24px',
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
  },
  tableWrap: { overflowX: 'auto' as const },
});

interface NewIrrigationDialogProps {
  open:    boolean;
  onClose: () => void;
  loteId:  string;
  moduleId?: string;
  onSaved?: (stockDeduct?: IrrigationStockDeductStatus) => void;
}

function NewIrrigationDialog({ open, onClose, loteId, moduleId, onSaved }: NewIrrigationDialogProps) {
  const t  = useT();
  const toast = useToast();
  const qc = useQueryClient();

  const [batchId, setBatchId] = useState('');
  const [method, setMethod] = useState<IrrigationMethod>('MANUAL');
  const [durationMin, setDurationMin] = useState('');
  const [flowRateLph, setFlowRateLph] = useState('');
  const [depthMm, setDepthMm] = useState('');
  const [meterStart, setMeterStart] = useState('');
  const [meterEnd, setMeterEnd] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => {
    fe.clear();
    setBatchId(''); setMethod('MANUAL'); setDurationMin(''); setFlowRateLph(''); setDepthMm('');
    setMeterStart(''); setMeterEnd(''); setDescription(''); setNotes(''); setError('');
  };

  const methodLabel: Record<IrrigationMethod, string> = {
    MANUAL: t.irrigation.methodManual,
    METER: t.irrigation.methodMedidor,
    SIMPLIFIED: t.irrigation.methodSimplificado,
  } as Record<IrrigationMethod, string>;

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateIrrigationInput = {
        batchId: batchId.trim(),
        moduleId,
        method,
        durationMin: durationMin ? parseFloat(durationMin) : undefined,
        flowRateLph: flowRateLph ? parseFloat(flowRateLph) : undefined,
        depthMm: depthMm && parseFloat(depthMm) > 0 ? parseFloat(depthMm) : undefined,
        meterReadings: (meterStart && meterEnd)
          ? { start: parseFloat(meterStart), end: parseFloat(meterEnd), unit: 'L' }
          : undefined,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      return createIrrigationLog(loteId, input);
    },
    onSuccess: (created) => {
      if (created.budgetWarning) toast.warn(t.budgetNotice.title, created.budgetWarning);
      qc.invalidateQueries({ queryKey: ['irrigationLogs', loteId] });
      qc.invalidateQueries({ queryKey: ['irrigationKpi', loteId] });
      reset();
      onSaved?.(created.stockDeduct);
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.irrigation.createError),
  });

  const handleSubmit = () => {
    setError('');
    if (!fe.validate({ batchId: !batchId.trim() && t.irrigation.batchRequired })) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="irrigation"
              voiceExample={t.telegramHelp.irrigationVoice}
              voiceDesc={t.telegramHelp.irrigationDesc}
            />
          }>{t.irrigation.newRecordTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={t.irrigation.fieldBatch} {...fe.props('batchId')}>
                  <CycleCombobox loteId={loteId} moduleId={moduleId} value={batchId} onChange={(v) => { setBatchId(v); fe.clear('batchId'); }} disabled={mut.isPending} />
                </Field>
                <Field label={t.irrigation.fieldMethod}>
                  <Dropdown
                    selectedOptions={[method]}
                    value={methodLabel[method]}
                    onOptionSelect={(_, d) => setMethod(d.optionValue as IrrigationMethod)}
                    disabled={mut.isPending}
                  >
                    <Option value="MANUAL">{t.irrigation.methodManual}</Option>
                    <Option value="METER">{t.irrigation.methodMedidor}</Option>
                    <Option value="SIMPLIFIED">{t.irrigation.methodSimplificado}</Option>
                  </Dropdown>
                </Field>
              </div>

              {method === 'MANUAL' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                  <Field label={t.irrigation.fieldDurationMin}>
                    <NumberInput value={durationMin} onValueChange={(v) => setDurationMin(v)} disabled={mut.isPending} />
                  </Field>
                  <Field label={t.irrigation.fieldFlowRate}>
                    <NumberInput value={flowRateLph} onValueChange={(v) => setFlowRateLph(v)} disabled={mut.isPending} />
                  </Field>
                </div>
              )}

              {(method === 'MANUAL' || method === 'SIMPLIFIED') && (
                <Field label={t.irrigation.fieldDepthMm}>
                  <Input type="number" value={depthMm} onChange={(_, d) => setDepthMm(d.value)} disabled={mut.isPending} />
                </Field>
              )}

              {method === 'METER' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                  <Field label={t.irrigation.fieldMeterStart}>
                    <NumberInput value={meterStart} onValueChange={(v) => setMeterStart(v)} disabled={mut.isPending} />
                  </Field>
                  <Field label={t.irrigation.fieldMeterEnd}>
                    <NumberInput value={meterEnd} onValueChange={(v) => setMeterEnd(v)} disabled={mut.isPending} />
                  </Field>
                </div>
              )}

              {method === 'SIMPLIFIED' && (
                <Field label={t.irrigation.fieldDescription}>
                  <Input value={description} onChange={(_, d) => setDescription(d.value)} disabled={mut.isPending} />
                </Field>
              )}

              <Field label={t.irrigation.fieldNotes}>
                <Input value={notes} onChange={(_, d) => setNotes(d.value)} disabled={mut.isPending} />
              </Field>

              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.irrigation.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Valor sentinela del Dropdown para el modo "Todos los lotes" (resumen cross-lote). */
const ALL_LOTES = '__all__';

export function IrrigationPage() {
  const s = useStyles();
  const t = useT();
  const { fmtNum, fmtDayMonth } = useIntlFormat();
  usePageTitle(t.pageTitle.irrigation);
  const { formatCurrency, formatDate } = useLocaleSettings();
  const { user } = useAuthStore();
  const { activeRanchoId } = useRanchoStore();
  // Roles con permiso irrigation:write (ver DEFAULT_ROLE_PERMISSIONS en rbac.model.ts)
  const canWrite = user?.roles.some(r => ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;
  const [selectedLoteId, setSelectedLoteId] = useState<string>('');
  const [newRecordOpen, setNewRecordOpen] = useState(false);
  const [stockWarning, setStockWarning] = useState<'FAILED' | 'PARTIAL' | null>(null);
  const isAllLotesMode = selectedLoteId === ALL_LOTES;

  const { data: allLotes = [], isLoading: lotesLoading, isError: lotesError } = useQuery<Lote[]>({ queryKey: ['lotes', activeRanchoId], queryFn: () => fetchLotes(activeRanchoId) });
  const lotes = onlyCampoLotes(allLotes as Lote[]);
  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: fetchRanchos, staleTime: 5 * 60_000 });
  const ranchoName = (id?: string) => (ranchos as any[]).find((r) => r.ranchoId === id)?.name as string | undefined;
  // Nombres de lote repetidos: se desambiguan con el rancho
  const loteLabel = (l: Lote) => {
    const dup = (lotes as Lote[]).filter((x) => x.name === l.name).length > 1;
    const rn = ranchoName(l.ranchoId);
    return dup && rn ? `${l.name} (${rn})` : l.name;
  };
  const { data: modules = [] } = useQuery({ queryKey: ['modules-rancho', activeRanchoId], queryFn: () => fetchModules({ ranchoId: activeRanchoId }) });
  // Árbol lote > zona > subzona; el selector de lote ahora también elige zonas.
  const lotZoneTree = useLoteZoneOptions((lotes as Lote[]).map((l) => ({ ...l, name: loteLabel(l) })), modules);
  const zoneOptions = [{ id: ALL_LOTES, label: t.irrigation.allLotes }, ...lotZoneTree];
  const selection = isAllLotesMode ? null : parseLoteZoneId(selectedLoteId, modules);
  const activeLoteId = isAllLotesMode ? '' : (selection?.loteId ?? ((lotes as Lote[])[0]?.loteId ?? ''));
  const activeModuleId = selection?.moduleId;
  const selectValue = isAllLotesMode ? ALL_LOTES : (activeLoteId ? loteZoneId({ loteId: activeLoteId, moduleId: activeModuleId }) : '');

  const { data: logs = [], isLoading: logsLoading } = useQuery<IrrigationLog[]>({
    queryKey: ['irrigationLogs', activeLoteId, activeModuleId ?? null],
    queryFn:  () => fetchIrrigationLogs(activeLoteId, 20, activeModuleId),
    enabled:  !!activeLoteId,
  });

  const { data: kpi, isLoading: kpiLoading, isError: kpiError } = useQuery({
    queryKey: ['irrigationKpi', activeLoteId, activeModuleId ?? null],
    queryFn:  () => fetchIrrigationKpi(activeLoteId, activeModuleId),
    enabled:  !!activeLoteId,
  });

  // Resumen cross-lote (rancho activo) — solo se pide en modo "Todos los lotes".
  const { data: recentLogs = [], isLoading: recentLoading } = useQuery<IrrigationLog[]>({
    queryKey: ['recentIrrigation', activeRanchoId],
    queryFn:  () => fetchRecentIrrigationLogs(50, activeRanchoId),
    enabled:  isAllLotesMode,
  });

  const volumeChart = logs.slice(-30).map((l) => ({
    date:   (l.irrigatedAt ?? '').slice(0, 10),
    litros: l.waterVolumeLiters ?? 0,
    lPerKg: l.litersPerKg ?? 0,
  }));

  const loteName = (id: string) => { const l = (lotes as Lote[]).find((x) => x.loteId === id); return l ? loteLabel(l) : id; };
  const who = (l: IrrigationLog) => l.recordedByName ?? l.operatorId ?? l.recordedBy ?? '';
  const sourceBadge = (l: IrrigationLog) => l.source ? (
    <Badge appearance="outline" color={l.source === 'TELEGRAM' ? 'brand' : 'informative'} size="small" style={{ marginLeft: 6 }}>
      {l.source === 'TELEGRAM' ? t.irrigation.sourceTelegram : t.irrigation.sourceWeb}
    </Badge>
  ) : null;

  // Agregacion cross-lote (rancho activo) sobre los logs ya cargados
  const allVolume = recentLogs.reduce((sum, l) => sum + (l.waterVolumeLiters ?? 0), 0);
  const allChart = [...recentLogs].reverse().slice(-30).map((l) => ({
    date: (l.irrigatedAt ?? '').slice(0, 10), litros: l.waterVolumeLiters ?? 0, lPerKg: l.litersPerKg ?? 0,
  }));
  const byLote = Object.values(recentLogs.reduce<Record<string, { lote: string; litros: number; riegos: number }>>((acc, l) => {
    const e = acc[l.plotId] ?? { lote: loteName(l.plotId), litros: 0, riegos: 0 };
    e.litros += l.waterVolumeLiters ?? 0; e.riegos += 1; acc[l.plotId] = e;
    return acc;
  }, {}));
  const hasEfficiency = allChart.some((d) => d.lPerKg > 0);

  /** Formatea timestamps del riegador como resumen legible */
  function formatTimestamps(ts?: IrrigationTimestamps): string {
    if (!ts) return '—';
    const parts: string[] = [];
    if (ts.inicioHidratacion)   parts.push(`💧${ts.inicioHidratacion}`);
    if (ts.inicioFertilizacion) parts.push(`🌿${ts.inicioFertilizacion}`);
    if (ts.finFertilizacion)    parts.push(`✅${ts.finFertilizacion}`);
    if (ts.finLavadoCintilla)   parts.push(`🔚${ts.finLavadoCintilla}`);
    return parts.length ? parts.join(' ') : '—';
  }

  return (
    <div className={s.root}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageTitle>{t.irrigation.title}</PageTitle>
        {canWrite && activeLoteId && (
          <Button appearance="primary" onClick={() => setNewRecordOpen(true)}>
            {t.irrigation.newRecord}
          </Button>
        )}
      </div>

      {stockWarning && (
        <MessageBar intent="warning" role="alert">
          <MessageBarBody>
            <MessageBarTitle>{t.irrigation.stockDeductWarnTitle}</MessageBarTitle>
            {stockWarning === 'FAILED' ? t.irrigation.stockDeductFailed : t.irrigation.stockDeductPartial}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={s.toolbar}>
        {lotesLoading ? (
          <Spinner size="small" />
        ) : lotesError ? (
          <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{t.irrigation.lotesLoadError}</Text>
        ) : (
          <SearchableSelect
            label={t.irrigation.zoneFilter}
            placeholder={t.irrigation.selectLot}
            options={zoneOptions}
            value={selectValue}
            onChange={setSelectedLoteId}
            noResultsText={t.common.searchNoResults}
          />
        )}
      </div>

      {!isAllLotesMode && (
      <>
      {/* KPIs */}
      {kpiLoading && <Spinner size="small" />}
      {kpiError && (
        <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{t.irrigation.kpiLoadError}</Text>
      )}
      {kpi && (
        <div className={s.kpiRow}>
          <KpiCard
            label={t.irrigation.kpiVolume}
            value={fmtNum(kpi.totalVolumeLiters)}
            sub={`${kpi.sessionCount} ${t.irrigation.kpiVolumeSub}`}
          />
          {kpi.avgLitersPerKg > 0 && (
            <KpiCard
              label={t.irrigation.kpiEfficiency}
              value={kpi.avgLitersPerKg.toFixed(1)}
              sub={t.irrigation.kpiEfficiencySub}
              accent={kpi.avgLitersPerKg > 100}
            />
          )}
          {kpi.lastIrrigatedAt && (
            <KpiCard label={t.irrigation.kpiLastIrrigation} value={formatDate(kpi.lastIrrigatedAt.slice(0, 10))} />
          )}
        </div>
      )}

      {/* Charts */}
      {volumeChart.length > 0 && (
        <div className={s.grid}>
          <div className={s.card}>
            <Text weight="semibold">{t.irrigation.chartVolume}</Text>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={volumeChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={fmtDayMonth} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => `${fmtNum(v)} L`} />
                <Bar dataKey="litros" fill={tokens.colorBrandBackground} name="Litros" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className={s.card}>
            <Text weight="semibold">{t.irrigation.chartEfficiency}</Text>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={volumeChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={fmtDayMonth} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => `${v.toFixed(1)} L/kg`} />
                <Legend />
                <Line type="monotone" dataKey="lPerKg" stroke="#E8A020" name="L/kg" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className={s.card}>
        <Text weight="semibold">{t.irrigation.tableTitle}</Text>
        {logsLoading ? (
          <Spinner size="small" />
        ) : logs.length === 0 ? (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.irrigation.noRecords}</Text>
        ) : (
          <div className={s.tableWrap}>
            <div style={{ minWidth: '1000px' }}>
              <DataTable<IrrigationLog>
                rows={logs}
                rowKey={(l) => l.logId}
                columns={[
                  { key: 'date', header: t.irrigation.tableDate, width: 1.3, render: (l) => (l.irrigatedAt ?? '').slice(0, 16).replace('T', ' ') },
                  { key: 'method', header: t.irrigation.tableMethod, render: (l) => <Badge appearance="tint" color="informative" size="small">{t.irrigation.methods[l.method as keyof typeof t.irrigation.methods] ?? l.method}</Badge> },
                  { key: 'volume', header: t.irrigation.tableVolume, align: 'right', render: (l) => l.waterVolumeLiters != null ? fmtNum(l.waterVolumeLiters) : '—' },
                  { key: 'duration', header: t.irrigation.tableDuration, align: 'right', width: 1.4, render: (l) => (l.methodData?.durationMin ?? l.durationMin) != null
                    ? <span style={{ whiteSpace: 'nowrap' }}>{l.methodData?.durationMin ?? l.durationMin}{(l.methodData?.flowRateLph ?? l.flowRateLph) != null && <span style={{ color: tokens.colorNeutralForeground3 }}> · {l.methodData?.flowRateLph ?? l.flowRateLph} {t.irrigation.flowUnit}</span>}</span>
                    : '—' },
                  { key: 'depth', header: t.irrigation.tableDepthMm, align: 'right', render: (l) => l.depthMm != null ? `${l.depthIsEstimated ? '≈ ' : ''}${l.depthMm}` : '—' },
                  { key: 'fert', header: t.irrigation.tableFert, render: (l) => l.fertInputs?.length ? (
                    <FluentTooltip
                      content={l.fertInputs.map(f =>
                        `${f.productName}: ${f.quantityUsed} ${f.quantityUnit}${f.costoTotal != null ? ` (${formatCurrency(f.costoTotal)})` : ''}`
                      ).join('\n')}
                      relationship="description"
                    >
                      <Badge appearance="tint" color="success" size="small">{l.fertInputs.length} fert.</Badge>
                    </FluentTooltip>
                  ) : (
                    <Badge appearance="tint" color="subtle" size="small">{t.common.no}</Badge>
                  ) },
                  { key: 'fertCost', header: t.irrigation.tableFertCost, align: 'right', render: (l) => l.fertCostoTotal != null ? formatCurrency(l.fertCostoTotal) : '—' },
                  { key: 'ts', header: t.irrigation.tableTimestamps, width: 1.4, render: (l) => (
                    <FluentTooltip
                      content={[
                        l.timestamps?.inicioHidratacion   ? `💧 ${t.irrigation.tsHydrationStart}: ${l.timestamps.inicioHidratacion}` : '',
                        l.timestamps?.inicioFertilizacion ? `🌿 ${t.irrigation.tsFertStart}: ${l.timestamps.inicioFertilizacion}` : '',
                        l.timestamps?.finFertilizacion    ? `✅ ${t.irrigation.tsFertEnd}: ${l.timestamps.finFertilizacion}` : '',
                        l.timestamps?.finLavadoCintilla   ? `🔚 ${t.irrigation.tsWashEnd}: ${l.timestamps.finLavadoCintilla}` : '',
                      ].filter(Boolean).join('\n') || '—'}
                      relationship="description"
                    >
                      <span style={{ whiteSpace: 'nowrap', fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{formatTimestamps(l.timestamps)}</span>
                    </FluentTooltip>
                  ) },
                  { key: 'by', header: t.irrigation.tableRecordedBy, width: 1.4, render: (l) => <span style={{ color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' }}>{who(l)}{sourceBadge(l)}</span> },
                ]}
              />
            </div>
          </div>

        )}
      </div>
      </>
      )}

      {isAllLotesMode && recentLogs.length > 0 && (
        <>
          <div className={s.kpiRow}>
            <KpiCard label={t.irrigation.kpiVolume} value={fmtNum(allVolume)} sub={`${recentLogs.length} ${t.irrigation.kpiVolumeSub}`} />
          </div>
          <div className={s.grid}>
            <div className={s.card}>
              <Text weight="semibold">{t.irrigation.chartVolume}</Text>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={allChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={fmtDayMonth} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `${fmtNum(v)} L`} />
                  <Bar dataKey="litros" fill={tokens.colorBrandBackground} name="Litros" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className={s.card}>
              <Text weight="semibold">{t.irrigation.chartEfficiency}</Text>
              {hasEfficiency ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={allChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={fmtDayMonth} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: number) => `${v.toFixed(1)} L/kg`} />
                    <Legend />
                    <Line type="monotone" dataKey="lPerKg" stroke="#E8A020" name="L/kg" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
              )}
            </div>
          </div>
          <div className={s.card}>
            <Text weight="semibold">{t.irrigation.chartByLote}</Text>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byLote}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="lote" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => `${fmtNum(v)} L`} />
                <Bar dataKey="litros" fill={tokens.colorBrandBackground} name="Litros" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Tabla cross-lote (rancho activo) — modo "Todos los lotes" */}
      {isAllLotesMode && (
        <div className={s.card}>
          <Text weight="semibold">{t.irrigation.tableTitle}</Text>
          {recentLoading ? (
            <Spinner size="small" />
          ) : recentLogs.length === 0 ? (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.irrigation.noRecords}</Text>
          ) : (
            <div className={s.tableWrap}>
              <div style={{ minWidth: '700px' }}>
                <DataTable<IrrigationLog>
                  rows={recentLogs}
                  rowKey={(l) => l.logId}
                  columns={[
                    { key: 'date', header: t.irrigation.tableDate, width: 1.3, render: (l) => (l.irrigatedAt ?? '').slice(0, 16).replace('T', ' ') },
                    { key: 'lote', header: t.irrigation.tableLote, render: (l) => loteName(l.plotId) },
                    { key: 'method', header: t.irrigation.tableMethod, render: (l) => <Badge appearance="tint" color="informative" size="small">{t.irrigation.methods[l.method as keyof typeof t.irrigation.methods] ?? l.method}</Badge> },
                    { key: 'volume', header: t.irrigation.tableVolume, align: 'right', render: (l) => l.waterVolumeLiters != null ? fmtNum(l.waterVolumeLiters) : '—' },
                    { key: 'depth', header: t.irrigation.tableDepthMm, align: 'right', render: (l) => l.depthMm != null ? `${l.depthIsEstimated ? '≈ ' : ''}${l.depthMm}` : '—' },
                    { key: 'by', header: t.irrigation.tableRecordedBy, width: 1.4, render: (l) => <span style={{ color: tokens.colorNeutralForeground3 }}>{who(l)}{sourceBadge(l)}</span> },
                  ]}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <NewIrrigationDialog open={newRecordOpen} onClose={() => setNewRecordOpen(false)} onSaved={(st) => setStockWarning(st === 'FAILED' || st === 'PARTIAL' ? st : null)} loteId={activeLoteId} moduleId={activeModuleId} />
    </div>
  );
}
